import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    taskTemplateContract,
    TaskTemplateBulkSave,
} from "@common/contracts/task-template.contract";
import { TaskTemplate, TaskTemplateSchema } from "@common/types/taskTemplate";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { organizationTaskTemplateQueryKeys } from "@api/organizationTaskTemplate.api";
import { getApiErrorStatus } from "./apiError";
import { fetchAllPages } from "./fetchAllPages";

const taskTemplateClient = getBackendAPIClient(taskTemplateContract);

const taskTemplateQueryKeys = {
    all: () => ["task-template"] as const,
    organization: (organizationId: string) =>
        [
            ...taskTemplateQueryKeys.all(),
            "organization",
            organizationId,
        ] as const,
    byCloseTemplate: (organizationId: string, closeTemplateId: string) =>
        [
            ...taskTemplateQueryKeys.organization(organizationId),
            "closeTemplate",
            closeTemplateId,
        ] as const,
};

export function useCloseTemplateTaskTemplates(closeTemplateId: string | null) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey:
            organizationId && closeTemplateId
                ? taskTemplateQueryKeys.byCloseTemplate(
                      organizationId,
                      closeTemplateId,
                  )
                : [],
        queryFn: async () => {
            if (!organizationId || !closeTemplateId) {
                return [] as TaskTemplate[];
            }

            return await fetchAllPages({
                fetchPage: async (offset) => {
                    const response = await taskTemplateClient.listTaskTemplates(
                        {
                            params: { organizationId },
                            query: { offset, closeTemplateId },
                        },
                    );

                    if (response.status !== 200) {
                        throw new Error("Failed to fetch task templates");
                    }

                    return {
                        items: response.body.taskTemplates.map((t) =>
                            TaskTemplateSchema.parse(t),
                        ),
                        hasMore: response.body.hasMore,
                    };
                },
            });
        },
        enabled: !!organizationId && !!closeTemplateId,
    });
}

export function useBulkSaveCloseTemplateTasksMutation(closeTemplateId: string) {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (body: TaskTemplateBulkSave) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }

            const response = await taskTemplateClient.bulkSaveTaskTemplates({
                params: { organizationId },
                body,
            });

            if (response.status !== 200) {
                throw new Error("Failed to save task templates");
            }

            return response.body;
        },
        onError: (error) => {
            if (!organizationId || getApiErrorStatus(error) !== 409) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey:
                    organizationTaskTemplateQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey: taskTemplateQueryKeys.byCloseTemplate(
                    organizationId,
                    closeTemplateId,
                ),
            });
            void queryClient.invalidateQueries({
                queryKey:
                    organizationTaskTemplateQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}
